/**
 * Bottom Navigation Component
 *
 * A mobile-style bottom tab bar navigation with:
 * - 4-item tab bar: Home, Explore, Recs, More
 * - Active state styling with visual indicators
 * - Badge support for pending recommendations count
 * - Integration with TanStack Router
 *
 * Based on the ticket: curie-compass-g2k
 */

import type { ElementType } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Home, Search, Lightbulb, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRecommendations } from "@/hooks";

// ============================================================================
// Types
// ============================================================================

interface NavItemConfig {
  /** The route path to navigate to */
  to: string;
  /** Display label for the tab */
  label: string;
  /** Icon component from lucide-react */
  icon: ElementType;
  /** Whether this tab should show a badge */
  showBadge?: boolean;
  /** Pattern to match for active state (supports multiple paths) */
  activePatterns?: string[];
}

interface BottomNavigationProps {
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const NAV_ITEMS: NavItemConfig[] = [
  {
    to: "/",
    label: "Home",
    icon: Home,
    activePatterns: ["/"],
  },
  {
    to: "/explore",
    label: "Explore",
    icon: Search,
    activePatterns: ["/explore", "/funds"],
  },
  {
    to: "/recommendations",
    label: "Recs",
    icon: Lightbulb,
    showBadge: true,
    activePatterns: ["/recommendations"],
  },
  {
    to: "/holdings",
    label: "More",
    icon: MoreHorizontal,
    activePatterns: ["/holdings", "/compare"],
  },
];

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Badge component for showing notification count
 */
function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null;

  // Format count for display (99+ for large numbers)
  const displayCount = count > 99 ? "99+" : count.toString();

  return (
    <span
      className={cn(
        "absolute -top-1 -right-1 flex items-center justify-center",
        "min-w-[18px] h-[18px] px-1",
        "text-[10px] font-semibold leading-none",
        "bg-rose-500 text-white rounded-full",
        "ring-2 ring-background"
      )}
    >
      {displayCount}
    </span>
  );
}

/**
 * Individual navigation item
 */
function NavItemComponent({
  item,
  isActive,
  badgeCount,
}: {
  item: NavItemConfig;
  isActive: boolean;
  badgeCount?: number;
}) {
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      className={cn(
        "relative flex flex-1 flex-col items-center justify-center gap-1",
        "py-2 px-1 min-w-0",
        "text-xs font-medium transition-colors duration-150",
        isActive
          ? "text-primary"
          : "text-muted-foreground hover:text-foreground"
      )}
      aria-current={isActive ? "page" : undefined}
    >
      {/* Active indicator bar */}
      {isActive && (
        <span
          className={cn(
            "absolute top-0 left-1/2 -translate-x-1/2",
            "h-0.5 w-8 rounded-full bg-primary"
          )}
        />
      )}

      {/* Icon container with badge */}
      <span className="relative">
        <Icon
          className={cn(
            "h-5 w-5 transition-transform duration-150",
            isActive && "scale-110"
          )}
        />
        {item.showBadge && badgeCount !== undefined && badgeCount > 0 && (
          <NavBadge count={badgeCount} />
        )}
      </span>

      {/* Label */}
      <span
        className={cn(
          "truncate max-w-full",
          isActive && "font-semibold"
        )}
      >
        {item.label}
      </span>
    </Link>
  );
}

// ============================================================================
// Helper function for checking active state
// ============================================================================

/**
 * Check if current path matches any of the active patterns
 */
function isPathActive(patterns: string[] | undefined, currentPath: string): boolean {
  if (!patterns) return false;

  return patterns.some((pattern) => {
    // Exact match for root path
    if (pattern === "/") {
      return currentPath === "/";
    }
    // Prefix match for other paths
    return currentPath === pattern || currentPath.startsWith(`${pattern}/`);
  });
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Bottom Navigation
 *
 * A mobile-style bottom navigation bar with 4 tabs: Home, Explore, Recs, More.
 * Includes active state styling and a badge indicator on the Recs tab for
 * pending recommendations.
 *
 * @example
 * ```tsx
 * // In your root layout
 * function RootLayout() {
 *   return (
 *     <div className="min-h-screen pb-16">
 *       <main><Outlet /></main>
 *       <BottomNavigation />
 *     </div>
 *   );
 * }
 * ```
 */
export function BottomNavigation({ className }: BottomNavigationProps) {
  // Get current route path
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  // Get pending recommendations count for badge
  const { counts } = useRecommendations();
  const pendingCount = counts.total;

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50",
        "bg-background/95 backdrop-blur-md",
        "border-t border-border",
        "safe-area-inset-bottom",
        className
      )}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="flex items-stretch h-16 max-w-lg mx-auto">
        {NAV_ITEMS.map((item) => {
          const isActive = isPathActive(item.activePatterns, currentPath);
          const badgeCount = item.showBadge ? pendingCount : undefined;

          return (
            <NavItemComponent
              key={item.to}
              item={item}
              isActive={isActive}
              badgeCount={badgeCount}
            />
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Skeleton loader for BottomNavigation
 * Useful when the navigation needs to appear before data is loaded
 */
export function BottomNavigationSkeleton({ className }: { className?: string }) {
  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50",
        "bg-background/95 backdrop-blur-md",
        "border-t border-border",
        "safe-area-inset-bottom",
        className
      )}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="flex items-stretch h-16 max-w-lg mx-auto">
        {NAV_ITEMS.map((item) => (
          <div
            key={item.to}
            className="flex flex-1 flex-col items-center justify-center gap-1 py-2 px-1"
          >
            <div className="h-5 w-5 rounded bg-muted animate-pulse" />
            <div className="h-3 w-8 rounded bg-muted animate-pulse" />
          </div>
        ))}
      </div>
    </nav>
  );
}

export default BottomNavigation;
